import { Box } from '@mui/material';
import { getFullName } from '@provision/formatting-utils';
import { RichTextEditorFile } from '@provision/rich-text-editor';
import DOMPurify from 'dompurify';
import { memo, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { PartnerTimeZoneTooltip } from 'src/components';
import { ReactionsFooter, useReactionPicker } from 'src/components/Reactions';
import { REPLY_ROW_HOVER_TARGET_CLASS, ReplyRow } from 'src/components/Replies';
import { useTextWithUI } from 'src/components/TextWithUI/use-text-with-ui';
import { useGroupRules, UserGroupRules } from 'src/graphql';
import { useConversationPrivateMessageUpdate } from 'src/graphql/entities/conversation-message/hooks/use-conversation-private-message-update';
import { useMessageReactions } from 'src/graphql/entities/conversation-message-reaction';
import { useMessageReply } from 'src/graphql/entities/conversation-message-reply';
import { useCurrentUserProfile } from 'src/graphql/entities/platform-user';
import { ChatMessageContext } from 'src/pages/Conversations/Chat/ChatMessage/chat-message-context';
import { ChatMessageEditMode } from 'src/pages/Conversations/Chat/ChatMessage/chat-message-edit-mode';
import {
  MessageToolbar,
  MessageToolbarAction,
} from 'src/pages/Conversations/Chat/ChatMessage/message-toolbar';
import { useChatCommonData } from 'src/pages/Conversations/Chat/registry/common-data-context';
import { useDisplayTime } from 'src/utils/hooks';
import { ReplyAvatar } from './reply-avatar';

const UNKNOWN_AUTHOR_NAME = 'Unknown';
const NO_ATTACHMENTS: RichTextEditorFile[] = [];
const doNothing = () => undefined;

interface ReplyItemProps {
  replyId: string;
  isResolution: boolean;
  readonly: boolean;
  onResolve: (replyId: string) => void;
  onUnresolve: () => void;
}

/** One reply with its reactions, in-place editing and hover actions. */
export const ReplyItem = memo(
  ({ replyId, isResolution, readonly, onResolve, onUnresolve }: ReplyItemProps) => {
    const reply = useMessageReply(replyId);
    const { user } = useCurrentUserProfile();
    const { displayTime } = useDisplayTime();
    const { metaData } = useContext(ChatMessageContext);
    const { checkValidity } = useGroupRules();
    const canEditNotes = checkValidity([UserGroupRules.AccessToEditInternalNotes]);
    const { updatePrivateMessage } = useConversationPrivateMessageUpdate();

    const [isEditing, setIsEditing] = useState(false);

    const reactionsEnabled = useChatCommonData((s) => s.reactionsEnabled);
    const { reactions, isReactable, addReaction, toggleReaction } =
      useMessageReactions(replyId);
    const rowRef = useRef<HTMLDivElement>(null);
    const reactionsRef = useRef<HTMLDivElement>(null);
    // A reply is added optimistically with an empty sender channel id until the server has stored it.
    const isStored = reply?.senderChannel?.id !== '';
    const canReact = reactionsEnabled && isReactable && isStored && !readonly && !isEditing;
    /* The reactions row handles its own presses: a long press on a chip shows who reacted. */
    const reactionPicker = useReactionPicker({
      canReact,
      anchorRef: rowRef,
      ignoredRefs: [reactionsRef],
    });
    const {
      text,
      handlers: textHandlers,
      parentStyles,
    } = useTextWithUI(reply?.body ?? '', metaData);
    // Bodies can arrive from the Public API as arbitrary HTML; sanitize what is rendered.
    const safeHtml = useMemo(() => DOMPurify.sanitize(text), [text]);
    const bodyHtml = useMemo(() => ({ __html: safeHtml }), [safeHtml]);

    const author = reply?.senderChannel?.conversationParticipant?.platformUser ?? null;
    const isOwnReply = Boolean(author && author.userId === user?.userId);
    const handleCancelEdit = useCallback(() => setIsEditing(false), []);

    const handleSaveEdit = useCallback(
      (newBody: string, newAttachments: RichTextEditorFile[]) => {
        setIsEditing(false);
        if (!reply || newBody === reply.body) {
          return;
        }
        void updatePrivateMessage(
          {
            message: newBody,
            attachments: newAttachments,
            editedAt: new Date().toISOString(),
          },
          {
            id: reply.id,
            partnerId: reply.partnerId,
            conversationId: reply.conversationId,
            createdAt: reply.createdAt,
            attachments: [],
          },
        );
      },
      [reply, updatePrivateMessage],
    );

    const actions = useMemo<MessageToolbarAction[]>(() => {
      if (readonly) {
        return [];
      }
      if (isResolution) {
        return [{ label: 'Unresolve', icon: 'rotate-ccw', onClick: onUnresolve }];
      }
      return [
        ...(isOwnReply && canEditNotes
          ? [{ label: 'Edit reply', icon: 'pencil', onClick: () => setIsEditing(true) }]
          : []),
        {
          label: 'Resolve thread with this reply',
          icon: 'circle-check',
          onClick: () => onResolve(replyId),
        },
      ];
    }, [canEditNotes, isOwnReply, isResolution, onResolve, onUnresolve, readonly, replyId]);

    if (!reply) {
      return null;
    }

    const authorName = getFullName(author) || UNKNOWN_AUTHOR_NAME;

    return (
      <ReplyRow
        avatar={
          <ReplyAvatar
            name={authorName}
            partnerId={author?.partnerId}
            avatarFileKey={author?.avatarFileKey}
          />
        }
        authorName={authorName}
        timestamp={
          <PartnerTimeZoneTooltip date={reply.createdAt} partnerId={reply.partnerId}>
            {displayTime(reply.createdAt)}
          </PartnerTimeZoneTooltip>
        }
        isEdited={Boolean(reply.editedAt)}
        isResolution={isResolution}
        actions={
          isEditing ? null : (
            <MessageToolbar
              className={REPLY_ROW_HOVER_TARGET_CLASS}
              messageHtml={safeHtml}
              showMenu={false}
              isMenuOpen={false}
              onMenuClick={doNothing}
              actions={actions}
              onAddReactionClick={canReact ? reactionPicker.handleAddReactionClick : undefined}
              isReactionPickerOpen={reactionPicker.isOpen}
            />
          )
        }
        rootProps={{
          ...reactionPicker.longPressHandlers(),
          ref: rowRef,
        }}
        footer={
          reactionsEnabled ? (
            <ReactionsFooter
              reactionsRef={reactionsRef}
              picker={reactionPicker}
              reactions={reactions}
              canReact={canReact}
              onToggle={toggleReaction}
              onSelect={addReaction}
            />
          ) : null
        }
      >
        {isEditing ? (
          <ChatMessageEditMode
            onClose={handleCancelEdit}
            onSave={handleSaveEdit}
            content={reply.body}
            attachments={NO_ATTACHMENTS}
            partnerId={reply.partnerId}
            inline
          />
        ) : (
          <Box
            sx={[{ typography: 'body2', wordBreak: 'break-word' }, parentStyles]}
            dangerouslySetInnerHTML={bodyHtml}
            onClick={textHandlers.onClick}
          />
        )}
      </ReplyRow>
    );
  },
);

ReplyItem.displayName = 'ReplyItem';
